import ConfigPanel from 'dashboard/apps/InquiryTemplateEditor/ConfigPanel';
import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import useIsEditableWithOptions, {
  EditorAccessTier,
} from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import { getWorkflowStepPath } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import WorkflowConfigurationConditional from 'dashboard/apps/InquiryTemplateEditor/WorkflowStepPanel/WorkflowConfigurationConditional';
import { WorkflowError } from 'dashboard/models/workflow';
import { WorkflowStepConditional, WorkflowVersion } from 'dashboard/models/workflow-version';

const BODY_PADDING = 32;

interface WorkflowStepPanelProps {
  step: WorkflowStepConditional;
  basePath: string;
  errors: WorkflowError[];
  workflowVersion: WorkflowVersion;
  conditionalRouteToEdit: number;
  editable?: boolean;
  setConditionalRouteToEdit: (routeNumber: number) => void;
  handleClose: () => void;
}

const WorkflowStepPanel = ({
  step,
  errors,
  workflowVersion,
  conditionalRouteToEdit,
  editable,
  setConditionalRouteToEdit,
  handleClose,
}: WorkflowStepPanelProps) => {
  const { 'steps-config': steps } = workflowVersion.attributes;
  const stepPath = getWorkflowStepPath(step.token, steps);
  const name = `${workflowVersion.id}.attributes.steps-config.${stepPath}`;
  // edit permissions:
  // - inquiries.basic: can edit *only* the existing route conditions
  // - inquiries.enhanced: full editing capabilities
  const editableForBasicTier = useIsEditableWithOptions({
    accessTier: EditorAccessTier.InquiriesBasic,
  });
  const hasAccessForBasicTier = useHasAccessForTierDeterminator()(EditorAccessTier.InquiriesBasic);

  // toggle padding when fully editable to account for draggable item
  const bodyPadding = editable ? 0 : BODY_PADDING;
  return (
    <ConfigPanel
      panelType='workflow'
      name='workflowVersions'
      fieldsBase={name}
      title={step.name}
      description='Perform conditional actions based on specified events.'
      handleClose={handleClose}
      headerPadding={BODY_PADDING}
      bodyPadding={bodyPadding}
      editable={!!editable}
    >
      <WorkflowConfigurationConditional
        name={name}
        step={step}
        stepsConfig={steps}
        errors={errors}
        conditionalRouteToEdit={conditionalRouteToEdit}
        conditionalsEditable={editableForBasicTier}
        isLimited={!hasAccessForBasicTier}
        editable={!!editable}
        workflowVersionId={workflowVersion.id}
        setConditionalRouteToEdit={setConditionalRouteToEdit}
      />
    </ConfigPanel>
  );
};

export default WorkflowStepPanel;
